import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import type { AuthResponse, CurrentSession, InvitePreview } from '@contracts/index';

import { ApiError, api, setAuthToken, setForbiddenHandler } from './api';
import { initials } from './format';
import { CheckIcon, ChevronDownIcon, WarningIcon } from './icons';
import { gatheringOf } from './gathering';
import type { LaunchIntent } from './launch';
import { usePoll } from './liveness';
import ChoirScreen from './screens/ChoirScreen';
import Fade from './screens/Fade';
import MeScreen from './screens/MeScreen';
import Onboarding from './screens/Onboarding';
import SessionsScreen from './screens/SessionsScreen';
import Unfold from './screens/Unfold';
import Waiting from './screens/Waiting';
import TabBar, { type View } from './TabBar';
import { askWriteAccess } from './telegram';
import { identify } from './tracking';

/**
 * The shell. Ported from `legacy/frontend/src/App.tsx` at legacy commit 2953e3f; the slices
 * after the re-pin of `FND-906` port against `d5aa0fa`.
 *
 * **Slice 0 was the shell and only the shell.** What it left out has since arrived, each
 * with the flow that owns it:
 *
 * - the first pane is F-004's list, and stopped being a placeholder at that slice's T11; the
 *   second is F-016's hub, from that slice's T7; the third, «Я», is F-021's, from its T5.
 *
 * At slice 0 their stages existed and were reached, and what they rendered was a marked
 * placeholder. That was an **incomplete slice, not a parity deviation**: nothing claimed to
 * reproduce them, and `docs/parity/MATRIX.md` recorded F-000 as the only row that slice
 * touched. See `docs/specs/legacy/F-000/spec.md`.
 *
 * The current-session poll, the dot it lights on the first tab and the dissolve between
 * the panes arrived with **F-004**, which is where F-000's slice deferred them — build
 * spec §8, AC-65 to AC-68.
 */

/** A code the server does not know: rotated away, or mistyped by whoever passed it on. */
type InviteRefused = { choir_name: null; standing: 'invalid' };

// A singer sitting on «Спевки» learned that a rehearsal had opened only by switching tabs
// or restarting: the regent's panel polls its own endpoint, but nothing asked on this
// side. Ten seconds, not two - a singer is waiting for one event, the regent is watching
// people arrive.
const SESSION_POLL_MS = 10_000;
// The role arrives in the auth response, and a promotion refuses nothing - so unlike a
// demotion there is no signal to catch, only the asking. A minute is enough: the
// appointed regent is usually told out loud first.
const AUTH_POLL_MS = 60_000;

type Stage =
  | { kind: 'starting' }
  | { kind: 'outside_telegram' }
  | { kind: 'error'; message: string }
  | { kind: 'onboarding' }
  /**
   * The app is shut and there is nothing on this side to do about it: the person is
   * waiting at the door, or the regent has taken them out of the choir for a while. Not
   * an error — the server answers 200 with no token precisely so that this is not drawn
   * as a breakage.
   */
  | { kind: 'waiting'; standing: 'pending' | 'paused' }
  | { kind: 'ready' };

export default function App({
  launchIntent,
  getInitDataRaw,
}: {
  /**
   * Parsed at module load, before the SDK's reload strips it. The attendance token
   * travels to `SessionsScreen`, which hands it to the evening's own screen.
   */
  launchIntent: LaunchIntent;
  /**
   * Handed in rather than imported, so a test can render this screen without a Telegram
   * bridge. In the app it is always `telegram.getInitDataRaw`.
   */
  getInitDataRaw: () => string | null;
}) {
  const [stage, setStage] = useState<Stage>({ kind: 'starting' });
  const [auth, setAuth] = useState<AuthResponse | null>(null);
  // undefined — the server has not been asked yet: until it answers we can claim neither
  // "a rehearsal is running" nor "there is none".
  const [session, setSession] = useState<CurrentSession | null | undefined>(undefined);
  const [view, setView] = useState<View>('sessions');
  /** How many times the lit tab has been tapped. The screens are what act on it. */
  const [home, setHome] = useState(0);
  /**
   * The running gathering this launch has opened for the singer — C-010. Here and not in
   * `SessionsScreen`, which every tap on another tab unmounts.
   */
  const [seenLive, setSeenLive] = useState<string | null>(null);
  // Telegram keeps the webview alive when the app is swiped away, so «the next launch» of
  // C-010 §3.1 seldom came: the regent who left the panel once never saw it again. Coming
  // back to the app re-arms it, and so does coming back to «Спевки» from another tab
  // (`onSelect` below) — C-010 §3.4, from Vitali's check on the stand, 2026-10-08. Not
  // `useWake`: that also fires on `focus`, which a closing popup gives the list.
  //
  // C-020 (`FND-1615`): the same return brings someone on another tab to «Расписание», when
  // the poll's fresh answer shows a gathering — a спевка or an участие — that this webview has
  // never put on screen. `shownLive` is never re-armed: a gathering already seen and left for
  // «Хор» or «Я» is a choice, and a form there must not be pulled out from under its owner.
  const shownLive = useRef(new Set<string>());
  const returning = useRef(false);
  const onSeenLive = useCallback((sessionId: string) => {
    shownLive.current.add(sessionId);
    setSeenLive(sessionId);
  }, []);
  useEffect(() => {
    const back = (): void => {
      if (document.visibilityState !== 'visible') return;
      setSeenLive(null);
      returning.current = true;
    };
    document.addEventListener('visibilitychange', back);
    return () => document.removeEventListener('visibilitychange', back);
  }, []);
  /**
   * The gathering the launch named, until «Спевки» has opened it or found it gone — C-021
   * (`FND-1616`): the reminder's button carries the evening it is about. Here and not in
   * `SessionsScreen` for `seenLive`'s reason: that screen is rebuilt on every visit to the tab.
   */
  const [target, setTarget] = useState<string | null>(() => gatheringOf(launchIntent));
  const targetDone = useCallback(() => setTarget(null), []);
  /** The choir list, open under the header. Only ever reachable with two of them. */
  const [switching, setSwitching] = useState(false);
  // An invite has to survive the first authentication. Onboarding used to be shown only to
  // somebody with no choir at all, so a singer already in one tapped a link to a second
  // choir, the app opened on the old one, and nothing else happened - there is no other way
  // in, the code cannot be typed anywhere. A ref rather than state: applyAuth reads it at
  // call time, and completeOnboarding clears it in the same breath as applying the join.
  const pendingInvite = useRef<string | null>(
    launchIntent.kind === 'invite' ? launchIntent.inviteCode : null,
  );
  // What that code opens, once the server has said so. State rather than a ref: the choir's
  // name and «you were removed from it» are drawn on the screen. `invalid` is ours rather
  // than the server's - it is the refusal turned into one of the states this screen has to
  // draw.
  const [invitePreview, setInvitePreview] = useState<InvitePreview | InviteRefused | null>(null);

  const refreshSession = useCallback(async (): Promise<CurrentSession | null> => {
    try {
      const fresh = await api.currentSession();
      setSession(fresh);
      return fresh;
    } catch {
      // Keep what is known: the poll lands here on any hiccup, and a singer mid-check-in
      // must not watch the rehearsal vanish because one request failed. Only the first
      // ask has nothing to keep and answers "none".
      setSession((current) => (current === undefined ? null : current));
      return null;
    }
  }, []);

  // Re-read yourself without touching the chosen screen: the name and the voice part
  // change while the app is open, and the header with the tabs must not keep showing
  // what the person has just rewritten. setAuth and not applyAuth on purpose: the latter
  // also moves you to another screen.
  const refreshAuth = useCallback(async () => {
    try {
      const response = await api.me();
      setAuthToken(response.token);
      setAuth(response);
      // The regent may have paused this person while they had the app open, and the poll
      // is where that arrives: every request from now on is refused, so the screen has to
      // become the one that says why rather than a wall of failures on whatever tab they
      // were looking at.
      if (response.token === null && response.standing !== 'active') {
        setStage({ kind: 'waiting', standing: response.standing });
      }
    } catch {
      // Not critical: the screen keeps the previous data until a restart.
    }
  }, []);

  const applyAuth = useCallback(
    async (response: AuthResponse) => {
      setAuthToken(response.token);
      setAuth(response);
      identify(response.telegram_user_id);
      // No token is not a failure and is checked before anything else: neither a `pending`
      // person nor a `paused` one has a choir to open, and both would otherwise fall into
      // onboarding and be offered a form that leads back to where they already are.
      if (response.token === null && response.standing !== 'active') {
        pendingInvite.current = null;
        setInvitePreview(null);
        setStage({ kind: 'waiting', standing: response.standing });
        return;
      }
      if (response.active_choir_id === null || pendingInvite.current !== null) {
        setStage({ kind: 'onboarding' });
        return;
      }
      // Nothing to choose between any more: the rehearsal that is running and the ones
      // ahead share one screen, so both launch intents - a QR and a reminder - land on the
      // same tab as an ordinary open.
      setView('sessions');
      setStage({ kind: 'ready' });
      void refreshSession();
    },
    [refreshSession],
  );

  /**
   * What the invite code opens, resolved before the join form is offered.
   *
   * A code cannot be turned into a choir on this side, so an invitation to a choir the
   * person is already in looked exactly like an invitation to a new one: the owner followed
   * his own link, was asked for a name and a voice part, and the server - which keeps the
   * name and the role a member already has - threw both away and only re-selected the
   * choir. Opening your own link to check it is the ordinary case, so it must not look like
   * registering again.
   */
  const settleInvite = useCallback(
    async (code: string, response: AuthResponse): Promise<AuthResponse> => {
      let preview: InvitePreview;
      try {
        preview = await api.invitePreview(code);
      } catch (error) {
        // A refused code is an answer, not a hiccup. The regent changes the link when one
        // leaks, and the old one lives on in the chat it was sent to: whoever taps it used
        // to be given the whole form and told «Приглашение не действует» only after filling
        // it in and pressing «Вступить». Anything else - the backend asleep, no network -
        // leaves the invite standing, and the form asks as before.
        if (error instanceof ApiError && error.failure?.error === 'invalid_invite') {
          setInvitePreview({ choir_name: null, standing: 'invalid' });
        }
        return response;
      }
      if (preview.standing !== 'member') {
        setInvitePreview(preview);
        return response;
      }
      pendingInvite.current = null;
      try {
        // Joining a choir you are in changes nothing but which choir opens - and that is
        // exactly what following the link asks for. Every profile field is null for the
        // same reason: the server keeps what an existing member already has, and sending
        // nulls here would be this call deciding it knows better.
        return await api.joinChoir(code, null, null, null);
      } catch {
        // Only the choice of choir is lost: the app opens on the last one.
        return response;
      }
    },
    [],
  );

  const start = useCallback(async (): Promise<void> => {
    // Substituted in development builds only. Otherwise a forgotten env variable ships to
    // production and sends debug data instead of the real thing: the server answers "open
    // it from Telegram" and the cause is far from obvious.
    const devInitData = import.meta.env.DEV ? import.meta.env.VITE_DEV_INIT_DATA : undefined;
    const initData = getInitDataRaw() ?? devInitData ?? null;
    if (initData === null || initData === '') {
      setStage({ kind: 'outside_telegram' });
      return;
    }
    setStage({ kind: 'starting' });
    try {
      let response = await api.authenticate(initData);
      // An invite the bot is holding for this person, when the launch carried none.
      // Following a link leaves the code in the chat with the bot, and only the button under
      // its greeting brings it here - the menu button beside the message field and the app's
      // line in the chat header open the same Mini App with nothing attached, and both used
      // to land somebody who was holding a link on «Нужна ссылка-приглашение». The server
      // answers with it only for somebody in no choir at all, so this cannot move anybody
      // who is already inside one.
      if (pendingInvite.current === null && response.pending_invite !== null) {
        pendingInvite.current = response.pending_invite;
      }
      if (pendingInvite.current !== null) {
        // The preview answers about the caller, so the token has to be in place before it
        // is asked - applyAuth would set it a step too late.
        setAuthToken(response.token);
        response = await settleInvite(pendingInvite.current, response);
      }
      await applyAuth(response);
    } catch (error) {
      setStage({
        kind: 'error',
        message: error instanceof ApiError ? error.message : 'Бэкенд недоступен',
      });
    }
  }, [applyAuth, getInitDataRaw, settleInvite]);

  // Synchronising with an external system is exactly what this is — the Telegram launch
  // data and the server — and the setState it ends in is the answer arriving, not a
  // value that could have been derived during render.
  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void start();
  }, [start]);

  // A demotion is invisible from here until something is refused: the role arrives once,
  // in the launch response, and the server re-reads it per request. Without this the app
  // went on drawing «Открыть спевку» and «Запланировать спевку», each tap answering «Это
  // доступно только регенту», with no way out but closing the app.
  //
  // One re-read at a time. A screen is several requests - the panel opened on the rig
  // sent six - and they are refused together, which without the guard meant six identical
  // /auth/me for one demotion.
  useEffect(() => {
    let rereading = false;
    setForbiddenHandler(() => {
      if (rereading) return;
      rereading = true;
      void refreshAuth().finally(() => {
        rereading = false;
      });
    });
    return () => setForbiddenHandler(null);
  }, [refreshAuth]);

  // The polls run only while something is on screen to follow them, pause while the
  // webview is hidden, and fire at once when it returns - which is the "closed and
  // reopened the app" everybody performed to get fresh data, minus the closing.
  const live = stage.kind === 'ready';
  usePoll(refreshSession, SESSION_POLL_MS, live);
  usePoll(refreshAuth, AUTH_POLL_MS, live);

  /**
   * Leave an invite unused. There is nowhere to go without a choir already open, so App
   * offers this only then - see onDismissInvite in Onboarding.
   */
  function dismissInvite(): void {
    pendingInvite.current = null;
    setInvitePreview(null);
    if (auth !== null) void applyAuth(auth);
  }

  async function completeOnboarding(response: AuthResponse): Promise<void> {
    const knocked = pendingInvite.current !== null;
    pendingInvite.current = null;
    setInvitePreview(null);
    await applyAuth(response);
    // Only the choir's creator is asked here. Somebody who came by a link was asked by
    // `Onboarding` before the request went out, because a link makes a request rather than a
    // member since 2026-08-27 and there is no token afterwards to store an answer with — see
    // `api.joinChoir`.
    if (knocked) return;
    // Telegram is asked only after the choir exists, so the answer is stored by a separate
    // request — the real answer, not an assumed one.
    const allowed = await askWriteAccess();
    try {
      await api.setWriteAccess(allowed);
    } catch {
      // Not critical: without the permission the reminders simply never arrive.
    }
  }

  async function switchChoir(choirId: number): Promise<void> {
    try {
      await applyAuth(await api.switchChoir(choirId));
    } catch {
      // Stay in the current choir.
    }
  }

  const activeChoir = auth?.choirs.find((choir) => choir.choir_id === auth.active_choir_id) ?? null;

  const ready = stage.kind === 'ready' && activeChoir !== null;
  const role = activeChoir?.role ?? null;

  // C-020: decided on the first answer after the return, not on what was known before the
  // webview went away — the gathering may have been opened or closed meanwhile. The rule is
  // C-010's (`SessionsScreen.tsx`, `unseen`): a regent always, a singer only while unmarked.
  useEffect(() => {
    if (!returning.current || session === undefined || role === null) return;
    returning.current = false;
    if (session === null || shownLive.current.has(session.session_id)) return;
    if (role !== 'regent' && session.my_mark !== null) return;
    // oxlint-disable-next-line react/set-state-in-effect
    setView('sessions');
  }, [session, role]);

  return (
    <>
      <main className={ready ? 'shell shell--tabbar' : 'shell'}>
        {/* No app name here: Telegram already shows it in its own header. The
            line belongs to the choir instead — which one you are looking at, and
            the switch when there is more than one. */}
        {activeChoir !== null && (
          <>
            <header className="header">
              {auth !== null && auth.choirs.length > 1 ? (
                // Two choirs is the case this exists for, and a native select
                // put the name of the current one inside a control that looked
                // like a field to be filled in. The list is ours: the choirs
                // with their initials and what you are in each of them.
                <button
                  className="choir-switch"
                  data-press="fade"
                  onClick={() => setSwitching(!switching)}
                >
                  <span className="label">{activeChoir.name}</span>
                  {/* One chevron, turned over. The button stays put across the
                      fold, so the icon in it is the same element before and
                      after and can be animated rather than swapped. */}
                  <ChevronDownIcon size={20} quiet turned={switching} />
                </button>
              ) : (
                <p className="label">{activeChoir.name}</p>
              )}
            </header>

            {auth !== null && (
              <Unfold open={switching}>
                <section className="card">
                  {auth.choirs.map((choir, index) => (
                    <Fragment key={choir.choir_id}>
                      {index > 0 && <span className="choir-divider" />}
                      <button
                        className="choir-option"
                        data-press="wash"
                        onClick={() => {
                          setSwitching(false);
                          if (choir.choir_id !== auth.active_choir_id) {
                            void switchChoir(choir.choir_id);
                          }
                        }}
                      >
                        <span
                          className={
                            choir.choir_id === auth.active_choir_id
                              ? 'avatar avatar--regent'
                              : 'avatar'
                          }
                        >
                          {initials(choir.name)}
                        </span>
                        <span className="live-body">
                          <span className="live-name">{choir.name}</span>
                          <span className="live-sub">
                            {choir.role === 'regent' ? 'регент' : 'певчий'}
                          </span>
                        </span>
                        {choir.choir_id === auth.active_choir_id && (
                          <span className="choir-current">
                            <CheckIcon size={20} />
                          </span>
                        )}
                      </button>
                    </Fragment>
                  ))}
                </section>
              </Unfold>
            )}
          </>
        )}

        {/* C-016 (FND-1611): shown only after 160ms, so a quick launch does not flash it. */}
        {stage.kind === 'starting' && <p className="hint hint--opening">Открываю…</p>}

        {stage.kind === 'outside_telegram' && (
          <section className="card">
            <h2>Открой из Telegram</h2>
            {/* The built Mini App is served from the backend's own address, so this URL
                opens in an ordinary browser for anybody the link was forwarded to. The
                setup line is for whoever runs the project. */}
            <p className="hint">
              Приложение опознаёт тебя по данным Telegram. Открой ссылку в Telegram, а не в
              браузере.
              {import.meta.env.DEV && (
                <>
                  {' '}
                  Для локальной разработки задай <code>VITE_DEV_INIT_DATA</code> в{' '}
                  <code>.env.development.local</code>.
                </>
              )}
            </p>
          </section>
        )}

        {stage.kind === 'error' && (
          <section className="card">
            <p className="notice notice--danger">
              <WarningIcon size={18} />
              Не получилось войти
            </p>
            <p className="failure">{stage.message}</p>
            {/* The only way out used to be closing the app and opening it again: a
                backend that was down for a minute cost a restart. */}
            <button className="btn btn--outline" onClick={() => void start()}>
              Попробовать снова
            </button>
          </section>
        )}

        {/* The whole app, for somebody it is shut to. No tab bar and no header — `ready`
            is what draws those, and there is nothing under this to navigate to. */}
        {stage.kind === 'waiting' && <Waiting standing={stage.standing} />}

        {stage.kind === 'onboarding' && (
          <Onboarding
            suggestedName={auth?.display_name ?? ''}
            invite={
              // Read during render as legacy `App.tsx:424` and `:427` read it: every write to
              // the ref is followed by a setState that redraws, and a state mirror would move
              // the read (plan §0.2 item 4).
              // oxlint-disable-next-line react/refs
              pendingInvite.current === null
                ? null
                : {
                    // oxlint-disable-next-line react/refs
                    code: pendingInvite.current,
                    choirName: invitePreview?.choir_name ?? null,
                    // «member» cannot reach this screen - settleInvite clears the invite for
                    // that person and sends them into the app - and if it ever did, the form
                    // it falls back to is what this screen showed everybody before the
                    // preview existed.
                    standing:
                      invitePreview === null || invitePreview.standing === 'member'
                        ? 'unknown'
                        : invitePreview.standing,
                  }
            }
            // Not knowing means not offering: a form that is certain to be refused is worse
            // than a hint about the invite link.
            mayCreate={auth?.may_create_choir ?? false}
            onDismissInvite={auth === null || auth.active_choir_id === null ? null : dismissInvite}
            onAuthenticated={(response) => void completeOnboarding(response)}
          />
        )}

        {stage.kind === 'ready' && activeChoir !== null && (
          // Keyed by choir: switching between two choirs where the role is the same left
          // the header renamed and the numbers below it belonging to the choir just left.
          // The screens load their data once on mount and React reuses the instance
          // without a key of its own.
          //
          // The role is in the key for the same reason. A screen that stays mounted
          // through a demotion keeps the refusal that revealed it - «Это доступно только
          // регенту» belongs to the tap that failed, not to the screen that replaces it.
          <div className="screen" key={`${activeChoir.choir_id}:${activeChoir.role}`}>
            {/* And one pane per tab inside that, which is the dissolve: the pane that
                replaces another one fades in over 140ms, and the first pane under a key
                of the choir's does not - a choir switch rebuilds everything here and is
                not a tab being tapped. A tab once opened stays mounted and hidden while
                another is up — C-023 (`FND-1618`), see `Fade.tsx`. */}
            <Fade
              at={view}
              panes={{
                sessions: (
                  <SessionsScreen
                    choir={activeChoir}
                    home={home}
                    session={session}
                    initialToken={launchIntent.kind === 'attendance' ? launchIntent.token : null}
                    seenLive={seenLive}
                    onSeenLive={onSeenLive}
                    target={target}
                    onTargetDone={targetDone}
                    onSessionChanged={() => void refreshSession()}
                  />
                ),
                choir: (
                  <ChoirScreen
                    choir={activeChoir}
                    home={home}
                    supportContact={auth?.support_contact ?? ''}
                  />
                ),
                me: (
                  <MeScreen
                    choir={activeChoir}
                    home={home}
                    // The name changes in the database while the auth response keeps
                    // the old one until the next launch: re-read it so the header and
                    // the tabs do not disagree with what was just typed.
                    onProfileChanged={() => void refreshAuth()}
                  />
                ),
              }}
            />
          </div>
        )}
      </main>

      {/* The strip the client hands over in fullscreen belongs to the notch and to
          Telegram's own floating buttons. Nothing of ours may be legible behind those,
          and until now everything was: the shell pads its content past the inset, but
          what scrolls keeps going up into it. Drawn unconditionally - outside fullscreen
          the inset is zero and so is this. */}
      <div className="top-veil" />

      {/* Outside the shell: the bar is fixed to the bottom of the screen, not part of
          what scrolls. */}
      {ready && activeChoir !== null && (
        <TabBar
          view={view}
          // The dot on the first tab. No request of its own: this is the same ten-second
          // poll the screens already run on.
          live={session != null}
          onSelect={(next) => {
            // The lit tab tapped again is «back to the top of this one», and the screens
            // are what act on it.
            if (next === view) setHome((count) => count + 1);
            else if (next === 'sessions') setSeenLive(null);
            setView(next);
            // The rehearsals screen tells the person whether one is running, so it asks
            // the server again on the way in: it may have been opened or closed while
            // another tab was up.
            if (next === 'sessions') void refreshSession();
          }}
        />
      )}
    </>
  );
}
